import {
  imageIdFormat,
  productFamilyIdFormat,
  productFamilyOptionIdFormat,
  productFamilyOptionValueIdFormat,
  productIdFormat,
} from "@redotech/redo-model/ids";
import { AttributeSchema } from "@redotech/redo-model/inventory/attributes";
import { ListingType } from "@redotech/redo-model/inventory/base-listing";
import { ListingsWithStatusSchema } from "@redotech/redo-model/inventory/listing-status";
import { ListingMappingSchema } from "@redotech/redo-model/inventory/listing-types";
import * as z from "zod/v4";

export const ProductFamilyMappingsSchema = z.object({
  shopifyMappings: ListingMappingSchema[ListingType.Shopify],
  redoMarketplaceMappings: ListingMappingSchema[ListingType.RedoMarketplace],
  chatGptMappings: ListingMappingSchema[ListingType.ChatGpt],
  ebayMappings: ListingMappingSchema[ListingType.Ebay],
});
export type ProductFamilyMappings = z.infer<typeof ProductFamilyMappingsSchema>;

const ImageSchema_ = z.object({
  id: imageIdFormat.schema,
  url: z.string(),
  alt_text: z.string(),
  position: z.number(),
  generated: z.boolean(),
  feedback: z.enum(["positive", "negative"]).nullable(),
});

const ProductInFamilySchema_ = z.object({
  id: productIdFormat.schema,
  title: z.string(),
  upc: z.string().nullish(),
  sku: z.string().nullish(),
  price: z.number().nullish(),
  currency: z.string().nullish(),
  image: ImageSchema_.nullish(),
  availableInventory: z.number(),
  options: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
    }),
  ),
  attributes: z.array(AttributeSchema),
});

const ProductOptionValueSchema_ = z.object({
  id: productFamilyOptionValueIdFormat.schema,
  value: z.string(),
});

export const ProductOptionValueSchema: z.ZodType<ProductOptionValue> =
  ProductOptionValueSchema_;
export interface ProductOptionValue
  extends z.infer<typeof ProductOptionValueSchema_> {}

const ProductOptionsSchema_ = z.object({
  id: productFamilyOptionIdFormat.schema,
  name: z.string(),
  values: z.array(ProductOptionValueSchema_),
});

export const ProductOptionsSchema: z.ZodType<ProductOptions> =
  ProductOptionsSchema_;
export interface ProductOptions extends z.infer<typeof ProductOptionsSchema_> {}

const ProductFamilyDetailsSchema_ = z.object({
  id: productFamilyIdFormat.schema,
  title: z.string(),
  description: z.string(),
  vendor: z.string().nullable(),
  tags: z.array(z.string()),
  created_at: z.date(),
  updated_at: z.date(),
  images: z.array(ImageSchema_),
  products: z.array(ProductInFamilySchema_),
  attributes: z.array(AttributeSchema),
  options: z.array(ProductOptionsSchema_),
});

export const ProductFamilyDetailsSchema: z.ZodType<ProductFamilyDetails> =
  ProductFamilyDetailsSchema_;
export interface ProductFamilyDetails
  extends z.infer<typeof ProductFamilyDetailsSchema_> {}

export const getProductFamilyDetails = {
  name: "getProductFamilyDetails" as const,
  input: z.object({ productFamilyId: productFamilyIdFormat.schema }),
  output: z.object({
    productFamily: ProductFamilyDetailsSchema.nullable(),
    mappings: ProductFamilyMappingsSchema,
    listings: ListingsWithStatusSchema,
  }),
};
